/**
 * 캐릭터 카드 내보내기 — 결정 #6(포맷 교환)의 임포터 역방향.
 *
 * 우리 도메인(Character + 선택적 Lorebook)을 Character Card V3 표준 JSON으로
 * 직렬화한다. 다른 앱(SillyTavern/Risu 등)과 호환되도록 표준 필드명을 쓴다.
 * 임베드 자산(아바타)은 바이트라 여기서 다루지 않는다 — 호출부(web)가 PNG로
 * 다시 임베드하거나 JSON만 내보낸다(결정 #18).
 *
 * 손실 방지: 임포트 때 `extensions`에 보존해 둔 미매핑 필드를 다시 펼쳐 넣어
 * 라운드트립을 보장한다.
 */
import type { Character, Lorebook, LorebookPosition } from "../domain/index.js";

/** CCv3 카드 객체(직렬화 가능한 평면 구조). */
export interface Ccv3Card {
  spec: "chara_card_v3";
  spec_version: "3.0";
  data: Record<string, unknown>;
}

/** 우리 LorebookPosition → CCv3 position 문자열. */
function positionToCcv3(pos: LorebookPosition): string {
  return pos.replace(/-/g, "_"); // before-char → before_char
}

/** Lorebook → CCv3 character_book. */
export function lorebookToCcv3(book: Lorebook): Record<string, unknown> {
  return {
    name: book.name,
    ...(book.description ? { description: book.description } : {}),
    scan_depth: book.scanDepth,
    recursive_scanning: book.recursiveScanning,
    ...(book.tokenBudget != null ? { token_budget: book.tokenBudget } : {}),
    entries: book.entries.map((e) => ({
      keys: e.keys,
      ...(e.secondaryKeys?.length ? { secondary_keys: e.secondaryKeys } : {}),
      content: e.content,
      constant: e.constant,
      enabled: !e.disabled,
      ...(e.caseSensitive != null ? { case_sensitive: e.caseSensitive } : {}),
      insertion_order: e.insertionOrder,
      position: positionToCcv3(e.position),
      ...(e.depth != null ? { depth: e.depth } : {}),
      ...(e.comment ? { comment: e.comment } : {}),
      ...(e.probability != null ? { priority: e.probability } : {}),
    })),
  };
}

/**
 * Character(+임베드 lorebook)를 CCv3 카드 객체로 변환한다.
 * `lorebook`이 주어지면 character_book으로 임베드한다.
 */
export function exportCardToCcv3(character: Character, lorebook?: Lorebook): Ccv3Card {
  const data: Record<string, unknown> = {
    name: character.name,
    description: character.description,
    first_mes: character.firstMessage,
    alternate_greetings: character.alternateGreetings,
    tags: character.tags,
  };

  if (character.personality) data["personality"] = character.personality;
  if (character.scenario) data["scenario"] = character.scenario;
  if (character.messageExample) data["mes_example"] = character.messageExample;
  if (character.systemPrompt) data["system_prompt"] = character.systemPrompt;
  if (character.postHistoryInstructions)
    data["post_history_instructions"] = character.postHistoryInstructions;
  if (character.creatorNotes) data["creator_notes"] = character.creatorNotes;

  // ShareMeta → CCv3 표준 필드(결정 #23).
  if (character.share?.author) data["creator"] = character.share.author;
  if (character.share?.creatorVersion) data["character_version"] = character.share.creatorVersion;
  if (character.share?.sourceUrl) data["source"] = character.share.sourceUrl;

  if (lorebook) data["character_book"] = lorebookToCcv3(lorebook);

  // 라운드트립: 임포트 때 보존한 미매핑 필드 복원(결정 #6).
  const ext = character.extensions;
  if (ext && typeof ext === "object") {
    // ccv3 하위에 보관해 둔 원본 extensions는 그대로 다시 넣는다.
    if (ext["ccv3"]) data["extensions"] = ext["ccv3"];
    for (const [k, v] of Object.entries(ext)) {
      if (k === "ccv3") continue;
      if (!(k in data)) data[k] = v;
    }
  }

  return { spec: "chara_card_v3", spec_version: "3.0", data };
}

/** CCv3 카드를 JSON 문자열로 직렬화(파일 저장용). */
export function exportCardToJson(character: Character, lorebook?: Lorebook): string {
  return JSON.stringify(exportCardToCcv3(character, lorebook), null, 2);
}
